import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Copy, KeyRound, Lock, Mic, Phone, Plus, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import salesService from '../../services/sales';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import type { PhoneDomain, PhoneDomainWithSecret, PhoneExtension, SalesPerson } from '../../types/sales';

/**
 * /sales/phone — the organisation's phone system (Sales Admin).
 *
 * A FusionPBX domain is connected here and gets a secret, shown once, which
 * goes into the bridge on the PBX (pbx-bridge/ in HHNodeServer). The server
 * works out the organisation from the domain; the bridge never says which.
 * Each extension can be given to a person, and ticked as part of the sales
 * group the incoming-call card goes to. Recording is switched on per domain;
 * how long recordings are kept is for the whole organisation.
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0';

/** The secret and the lines the bridge's config needs, shown once. */
function SecretPanel({ shown, onDone }: { shown: PhoneDomainWithSecret; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const config = [
    `FACTREE_DOMAIN=${shown.domain.domain_name}`,
    `FACTREE_SECRET=${shown.secret}`,
  ].join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(config);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 space-y-3 min-w-0" role="status">
      <p className="text-sm font-semibold text-emerald-900 break-words">
        The secret for {shown.domain.label}. It is shown this once.
      </p>
      <p className="text-sm text-emerald-900 break-words">
        Put these two lines in the bridge's settings file on the phone system (/etc/factree-pbx-bridge.env), then restart it.
        If it is lost, replace the secret and put the new one in.
      </p>
      <pre className="text-xs bg-white border border-emerald-200 rounded-lg p-3 overflow-x-auto whitespace-pre">{config}</pre>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={copy}><Copy className="h-4 w-4 mr-1" aria-hidden />{copied ? 'Copied' : 'Copy'}</Button>
        <Button size="sm" onClick={onDone}>I have put it in</Button>
      </div>
    </div>
  );
}

function ExtensionsCard({ domain, people, onChanged }: {
  domain: PhoneDomain; people: SalesPerson[]; onChanged: () => void;
}) {
  const [exts, setExts] = useState<PhoneExtension[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [userId, setUserId] = useState('');
  const [sales, setSales] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await salesService.listExtensions(domain.id);
      if (!res.success) throw res;
      setExts(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the extensions'));
    }
  }, [domain.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn() as { success?: boolean };
      if (res && res.success === false) throw res;
      await load();
      onChanged();
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!number.trim()) return;
    const ok = await run(() => salesService.addExtension(domain.id, {
      extension: number.trim(), label: label.trim() || null, user_id: userId ? Number(userId) : null, in_sales_group: sales,
    }));
    if (ok) { setNumber(''); setLabel(''); setUserId(''); setSales(false); }
  };

  const idFor = (s: string) => `ext-${domain.id}-${s}`;

  return (
    <div className="space-y-3 min-w-0">
      <h3 className="text-sm font-semibold text-neutral-800">Extensions</h3>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      {exts && exts.length === 0 && (
        <p className="text-sm text-neutral-600">No extensions yet. Add each one and who answers it, so calls are put against the right person.</p>
      )}
      {exts && exts.length > 0 && (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
          {exts.map((x) => (
            <li key={x.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
              <div className="min-w-0 sm:w-40">
                <span className="text-sm font-semibold text-neutral-900">{x.extension}</span>
                {x.label && <span className="block text-xs text-neutral-500 break-words">{x.label}</span>}
              </div>
              <label className="sr-only" htmlFor={`${idFor('user')}-${x.id}`}>Who answers extension {x.extension}</label>
              <select
                id={`${idFor('user')}-${x.id}`}
                value={x.user_id ?? ''}
                disabled={busy}
                onChange={(e) => run(() => salesService.updateExtension(x.id, { user_id: e.target.value ? Number(e.target.value) : null }))}
                className={`${inputCls} sm:w-56`}
              >
                <option value="">Nobody</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                {x.user_id && !people.some((p) => p.id === x.user_id) && (
                  <option value={x.user_id}>{x.user_name || x.user_email || `Person ${x.user_id}`}</option>
                )}
              </select>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={x.in_sales_group}
                  disabled={busy}
                  onChange={(e) => run(() => salesService.updateExtension(x.id, { in_sales_group: e.target.checked }))}
                />
                Sales calls
              </label>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                aria-label={`Remove extension ${x.extension}`}
                className="sm:ml-auto self-start"
                onClick={() => {
                  if (window.confirm(`Remove extension ${x.extension}? Calls already logged keep it.`)) {
                    run(() => salesService.deleteExtension(x.id));
                  }
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_1fr_auto_auto] gap-2 items-end min-w-0">
        <div className="min-w-0">
          <label htmlFor={idFor('new-number')} className="block text-xs font-medium text-neutral-600 mb-1">Extension</label>
          <input id={idFor('new-number')} inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} className={inputCls} placeholder="201" />
        </div>
        <div className="min-w-0">
          <label htmlFor={idFor('new-label')} className="block text-xs font-medium text-neutral-600 mb-1">Name (optional)</label>
          <input id={idFor('new-label')} value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls} placeholder="Front desk" />
        </div>
        <div className="min-w-0">
          <label htmlFor={idFor('new-user')} className="block text-xs font-medium text-neutral-600 mb-1">Who answers it</label>
          <select id={idFor('new-user')} value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls}>
            <option value="">Nobody yet</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-700 py-2">
          <input type="checkbox" checked={sales} onChange={(e) => setSales(e.target.checked)} />
          Sales calls
        </label>
        <Button type="submit" size="sm" isLoading={busy} disabled={!number.trim()}>
          <Plus className="h-4 w-4 mr-1" aria-hidden />Add
        </Button>
      </form>
    </div>
  );
}

/**
 * Recording for one domain. Switching on asks the admin to say callers are
 * told (Sales Module Terms §3): an announcement on the inbound routes. Until
 * it is on, FactreeTech refuses the bridge's recordings.
 */
function RecordingCard({ domain, tz, onChanged }: { domain: PhoneDomain; tz: string; onChanged: () => void }) {
  const [told, setTold] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `rec-${domain.id}`;

  const save = async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await salesService.setRecording(domain.id, enabled, told);
      if (!res.success) throw res;
      setTold(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-neutral-200 p-3 space-y-2 min-w-0">
      <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
        <Mic className="h-4 w-4" aria-hidden />Call recording
      </h3>
      {domain.recording_enabled ? (
        <>
          <p className="text-sm text-neutral-700 break-words">
            On{domain.recording_confirmed_at ? ` since ${formatDateTimeInTz(domain.recording_confirmed_at, tz)}` : ''}.
            Calls FusionPBX records are kept here with a transcript. A Sales Admin, or the person on the call, can play them.
          </p>
          <Button size="sm" variant="outline" isLoading={busy} onClick={() => save(false)}>Stop recording calls</Button>
        </>
      ) : (
        <>
          <p className="text-sm text-neutral-700 break-words">
            Off. Calls are logged, but recordings the phone system makes are not taken. Voicemail is kept either way.
          </p>
          <label htmlFor={id} className="flex items-start gap-2 text-sm text-neutral-800">
            <input id={id} type="checkbox" checked={told} onChange={(e) => setTold(e.target.checked)} className="mt-1 shrink-0" />
            <span className="break-words">
              Callers are told calls may be recorded before they are, for example with an announcement on our inbound routes.
              We meet the Telecommunications (Interception and Access) Act and our states' listening devices laws.
            </span>
          </label>
          <Button size="sm" isLoading={busy} disabled={!told} onClick={() => save(true)}>Record calls</Button>
        </>
      )}
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </div>
  );
}

const RETENTION_CHOICES: { value: string; label: string }[] = [
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '24', label: '2 years' },
  { value: '84', label: '7 years' },
  { value: 'forever', label: 'Keep them indefinitely' },
];

/** How long recordings, voicemail audio and transcripts are kept, for the whole organisation. */
function RetentionCard() {
  const [value, setValue] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    salesService.getSettings()
      .then((r) => { if (r.success) setValue(r.data.retention_months == null ? 'forever' : String(r.data.retention_months)); })
      .catch(() => setError('Could not load how long recordings are kept'));
  }, []);

  const save = async (next: string) => {
    setValue(next);
    setSaved(false);
    setError(null);
    try {
      const res = await salesService.saveSettings(next === 'forever' ? null : Number(next));
      if (!res.success) throw res;
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
    }
  };

  const known = value != null && RETENTION_CHOICES.some((c) => c.value === value);
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2 min-w-0">
      <h2 className="text-base font-semibold text-neutral-900">How long recordings are kept</h2>
      <p className="text-sm text-neutral-600 break-words">
        Call recordings, voicemail audio and their transcripts are deleted after this. The call log line stays.
      </p>
      <label htmlFor="retention" className="block text-xs font-medium text-neutral-600">Keep for</label>
      <select id="retention" value={value ?? ''} disabled={value == null} onChange={(e) => save(e.target.value)}
        className="w-full sm:w-64 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0">
        {!known && value != null && <option value={value}>{value} months</option>}
        {RETENTION_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      {value === 'forever' && (
        <p className="text-sm text-amber-800 break-words">
          Nothing is deleted. You are responsible for deleting what you no longer need, and for callers' requests to see or delete what you hold.
        </p>
      )}
      {saved && <p className="text-sm text-emerald-700">Saved.</p>}
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </section>
  );
}

function DomainCard({ domain, people, tz, onChanged, onSecret }: {
  domain: PhoneDomain; people: SalesPerson[]; tz: string; onChanged: () => void; onSecret: (s: PhoneDomainWithSecret) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<{ success: boolean; data?: unknown }>, after?: (data: unknown) => void) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.success) throw res;
      after?.(res.data);
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-neutral-900 break-words">{domain.label}</h2>
          <p className="text-sm text-neutral-600 break-all">{domain.domain_name}</p>
          <p className="text-xs text-neutral-500 break-words mt-1">
            {domain.active ? 'Connected' : 'Switched off: its calls are refused'}
            {' · '}secret ending {domain.secret_hint}
            {' · '}
            {domain.last_event_at
              ? `last heard from ${formatDateTimeInTz(domain.last_event_at, tz)}`
              : 'not heard from yet'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Replace the secret? The bridge is refused until it has the new one.')) {
                act(() => salesService.rotateSecret(domain.id), (d) => onSecret(d as PhoneDomainWithSecret));
              }
            }}
          >
            <KeyRound className="h-4 w-4 mr-1" aria-hidden />Replace secret
          </Button>
          <Button
            size="sm"
            variant={domain.active ? 'outline' : 'primary'}
            disabled={busy}
            onClick={() => act(() => salesService.updateDomain(domain.id, { active: !domain.active }))}
          >
            {domain.active ? 'Switch off' : 'Switch on'}
          </Button>
        </div>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      <RecordingCard domain={domain} tz={tz} onChanged={onChanged} />
      <ExtensionsCard domain={domain} people={people} onChanged={onChanged} />
    </section>
  );
}

function PhoneSettings() {
  const tz = useOrgTimezone();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('sales', 'admin');
  const [domains, setDomains] = useState<PhoneDomain[] | null>(null);
  const [people, setPeople] = useState<SalesPerson[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<PhoneDomainWithSecret | null>(null);

  const [label, setLabel] = useState('');
  const [domainName, setDomainName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await salesService.listDomains();
      if (!res.success) throw res;
      setDomains(res.data);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the phone system'));
    }
  }, []);

  useEffect(() => {
    if (!hasAccess) return;
    load();
    salesService.listPeople().then((r) => { if (r.success) setPeople(r.data); }).catch(() => {});
  }, [hasAccess, load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      const res = await salesService.addDomain(label.trim(), domainName.trim());
      if (!res.success) throw res;
      setShown(res.data);
      setLabel('');
      setDomainName('');
      await load();
    } catch (err) {
      setAddError(errorMessage(err, 'Could not connect the phone system'));
    } finally {
      setAdding(false);
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" aria-hidden />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Sales Admin seat required</h2>
          <p className="text-sm text-neutral-500">Setting up the phone system needs a Sales Admin seat.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Phone system"
        subtitle="Your FusionPBX domains, their extensions and who answers them"
        icon={<Phone className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {shown && <SecretPanel shown={shown} onDone={() => setShown(null)} />}
        {error && <p className="text-sm text-red-700 break-words">{error}</p>}

        {domains?.map((d) => (
          <DomainCard key={d.id} domain={d} people={people} tz={tz} onChanged={load} onSecret={setShown} />
        ))}

        {domains && domains.length > 0 && <RetentionCard />}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
          <h2 className="text-base font-semibold text-neutral-900">
            {domains && domains.length > 0 ? 'Connect another domain' : 'Connect your phone system'}
          </h2>
          <p className="text-sm text-neutral-600 break-words">
            The domain is the one your extensions are on in FusionPBX (Accounts → Domains). A domain can be connected to one organisation only.
            The bridge sends who called, who answered and how long the call was, and voicemail. Recordings are taken only once you switch recording on.
          </p>
          <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end min-w-0">
            <div className="min-w-0">
              <label htmlFor="phone-label" className="block text-xs font-medium text-neutral-600 mb-1">Name</label>
              <input id="phone-label" value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls} placeholder="Office phones" />
            </div>
            <div className="min-w-0">
              <label htmlFor="phone-domain" className="block text-xs font-medium text-neutral-600 mb-1">FusionPBX domain</label>
              <input id="phone-domain" value={domainName} onChange={(e) => setDomainName(e.target.value)} className={inputCls} placeholder="pbx.example.com.au" autoCapitalize="none" spellCheck={false} />
            </div>
            <Button type="submit" size="sm" isLoading={adding} disabled={!label.trim() || !domainName.trim()}>
              <Plus className="h-4 w-4 mr-1" aria-hidden />Connect
            </Button>
          </form>
          {addError && <p className="text-sm text-red-700 break-words">{addError}</p>}
        </section>
      </div>
    </>
  );
}

export default PhoneSettings;
